/**
 * useRealtime — side-effect hook that owns the RealtimeClient singleton lifecycle.
 *
 * Mounts when the user is authenticated and an organization is selected.
 * Tears down on unmount (logout, navigation away from AuthenticatedShell).
 * Switches location subscription via setLocation without full reconnect.
 */

import { type ReactElement, createElement, useEffect } from "react";
import { CalendarCheck, CalendarX, type LucideIcon, UserX } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/store/auth-store";
import { useAppStore } from "@/store/store";
import { realtimeClient } from "@/lib/realtime";
import type { RealtimeEvent } from "@/lib/realtime";
import { useShallow } from "zustand/react/shallow";
import type { QueryClient } from "@tanstack/react-query";
import type { AppStore } from "@/store/store";

// Local one-liner mirroring store.ts icon helper — see design D-2.
const icon = (C: LucideIcon): ReactElement => createElement(C, { size: 18, strokeWidth: 2.5 });

// KIND_TO_ICON constructed once at module load, not per WS frame.
// Unknown kinds return undefined naturally — graceful fallback (no crash).
const KIND_TO_ICON: Record<string, ReactElement> = {
  reservation_created_public: icon(CalendarCheck),
  reservation_cancelled_public: icon(CalendarX),
  no_show_detected: icon(UserX),
};

type NotifyFn = AppStore["notify"];

function deriveWsOrigin(): string {
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}`;
}

function dispatchRealtime(
  event: RealtimeEvent,
  qc: QueryClient,
  notify: NotifyFn,
  orgId: string,
): void {
  switch (event.type) {
    case "reservation.event":
      void qc.invalidateQueries({ queryKey: ["reservations", orgId] });
      void qc.invalidateQueries({ queryKey: ["suggest-tables"] });
      // Per-card waitlist availability hints also depend on occupancy,
      // so a seat / completion / cancellation elsewhere can flip a
      // waiting card from "fits" to "mesa ocupada" (or back).
      void qc.invalidateQueries({ queryKey: ["waitlist-dry-run"] });
      return;
    case "table.event":
      void qc.invalidateQueries({ queryKey: ["tables", orgId] });
      void qc.invalidateQueries({ queryKey: ["suggest-tables"] });
      void qc.invalidateQueries({ queryKey: ["waitlist-dry-run"] });
      return;
    case "guest.event":
      void qc.invalidateQueries({ queryKey: ["guests", orgId] });
      return;
    case "waitlist.event":
      // The queue list key is ``["waitlist", orgId, locationId]`` (see
      // useWaitlist) but the locationId can be the wildcard ``undefined``
      // in cache keys — broad invalidate by the leading "waitlist" tag
      // covers both the list and the per-entry table-suggestions cache,
      // and a seated event also touches reservations (a walk-in landed)
      // and the reservation suggestion cache (occupancy changed).
      void qc.invalidateQueries({ queryKey: ["waitlist"] });
      void qc.invalidateQueries({ queryKey: ["waitlist-table-suggestions"] });
      void qc.invalidateQueries({ queryKey: ["waitlist-dry-run"] });
      if (event.action === "seated") {
        void qc.invalidateQueries({ queryKey: ["reservations", orgId] });
        void qc.invalidateQueries({ queryKey: ["suggest-tables"] });
      }
      return;
    case "notification.staff":
      notify(
        event.severity,
        event.title,
        event.body,
        event.reservation_id,
        KIND_TO_ICON[event.kind], // undefined for unknown kinds → graceful fallback
      );
      return;
    default: {
      // Exhaustiveness check — UnknownEvent.type is `string`, so this branch
      // is the catch-all for unmatched literals. Drop silently.
      const _exhaustive: { type: string } = event;
      void _exhaustive;
      return;
    }
  }
}

export function useRealtime(): void {
  const { isAuthenticated, organizationId, locationId } = useAuthStore(
    useShallow((s) => ({
      isAuthenticated: s.isAuthenticated,
      organizationId: s.organizationId,
      locationId: s.locationId,
    })),
  );
  const queryClient = useQueryClient();
  const notify = useAppStore((s) => s.notify);

  // Mount/unmount effect — keyed on auth identity.
  // NOT keyed on locationId — setLocation handles runtime location switches
  // without tearing down the whole socket (see location effect below).
  useEffect(() => {
    if (import.meta.env.VITE_REALTIME_ENABLED === "false") return;
    if (!isAuthenticated || !organizationId) return;

    realtimeClient.configure({
      wsOrigin: deriveWsOrigin(),
      locationId,
      onMessage: (ev) => dispatchRealtime(ev, queryClient, notify, organizationId),
      onAuthFailure: () => useAuthStore.getState().setAuthenticated(false),
    });
    realtimeClient.connect();

    return () => {
      realtimeClient.close();
    };
  }, [isAuthenticated, organizationId, queryClient, notify]); // eslint-disable-line react-hooks/exhaustive-deps

  // Location effect — calls setLocation when locationId changes without
  // triggering a full socket teardown (the lifecycle effect above handles that).
  useEffect(() => {
    if (!isAuthenticated || !organizationId) return;
    realtimeClient.setLocation(locationId);
  }, [locationId, isAuthenticated, organizationId]);
}
